/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandModelos } from "@/store-zustand/modelos/modelos";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { handleGetModelosPorCliente } from "@/domain/use-cases/modelos";

export interface IPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface ICliente {
  id: number;
  nome: string;
  email: string;
  cnpj: string;
  cpf: string;
  telefone: string;
  tipo_pessoa: string;
}

export interface IProduto {
  id: number;
  nome: string;
  tecnologia: string;
  valor: string;
  estoque_atual: number;
}

export interface ICampoVariavel {
  id: number;
  nome: string;
  obrigatorio: boolean;
  descricao?: string;
}

export interface IModeloTecnico {
  id: number;
  nome_modelo: string;
  tipo_entrega: string;
  posicionamento: string;
  foto_frente_path: string;
  foto_verso_path: string;
  foto_frente_url: string;
  foto_verso_url: string;
  campo_chave: string;
  cliente: ICliente;
  produtos: IProduto;
  campos_variaveis: ICampoVariavel[];
  observacoes: string;
  ativo: boolean;
  created_at: string;
}

interface IModelosResponse {
  code: number;
  status: string;
  message: string;
  data: IModeloTecnico[];
  pagination: IPagination;
}

function mapModelosToTable(data: IModeloTecnico[]): Record<string, any>[] {
  return data?.map((modelo) => ({
    id: modelo.id,
    nome_modelo: modelo?.nome_modelo || "N/A",
    foto_frente_path: modelo.foto_frente_url || "N/A",
    foto_verso_path: modelo.foto_verso_url || "N/A",
    qtd_numeros_variaveis: modelo?.campos_variaveis?.length || "N/A",
    ativo: modelo?.ativo !== false,
    modeloCompleto: modelo,
  }));
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

type IUseModelosPorClienteOptions = {
  /** Lista também os inativos (gestão admin/produção/consultor); o dashboard do cliente não usa. */
  incluirInativos?: boolean;
};

function useModelosPorCliente(
  user_cliente_id: number,
  { incluirInativos = false }: IUseModelosPorClienteOptions = {}
) {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage, setTotalItemsPage } =
    useStoreZustandModelos();

  const { token: accessToken } = useStoreZustandUserAuth();

  // Chave de cache única baseada na página atual e na quantidade de itens por página
  const queryKey = ["modelos-por-cliente", currentPage, itemsPerPage, searchItem, incluirInativos];

  const {
    isLoading,
    error: isError,
    data: modelosPorClienteResponse,
  } = useQuery<IModelosResponse>({
    queryKey,
    queryFn: async () => {
      const response = await handleGetModelosPorCliente(
        accessToken!,
        user_cliente_id,
        currentPage,
        itemsPerPage,
        searchItem!,
        incluirInativos
      );
      return response;
    },
  });

  const modelosPorCliente = mapModelosToTable(modelosPorClienteResponse?.data as any);
  const pagination = modelosPorClienteResponse?.pagination;

  // só recalcula se currentPage ou last_page mudar
  const pages = useMemo(() => {
    return generatePagination(currentPage, pagination?.last_page ?? 1);
  }, [currentPage, pagination?.last_page]);

  // atualiza o estado global da aplicação uma única vez
  useEffect(() => {
    const total = Number(pagination?.total ?? 0);
    const perPage = pagination?.per_page ?? itemsPerPage;
    const resolvedLastPage = pagination?.last_page ?? Math.max(1, Math.ceil(total / perPage));

    setPaginations(pages as number[]);
    setLastPage(resolvedLastPage);
    setTotalItemsPage(total);
  }, [pages, setPaginations, setLastPage, pagination, setTotalItemsPage, itemsPerPage]);

  return {
    modelosPorClientes: modelosPorCliente ?? [],
    paginations: pages,
    isLoading,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useModelosPorCliente };
